import {
  clamp,
  clamp01,
  createAdvancedVoice,
  createBodyResonatorBank,
  createModalBank,
  createNoiseBurst,
  hashSeed,
  scheduleSafeEnvelope
} from "./dspPrimitives";
import { getVariantModelProfile } from "./instrumentModelProfiles";
import type { AdvancedSynthesisInput, AdvancedVoiceHandle, ModalSpec, ResonatorSpec } from "./types";

function controlNumber(input: AdvancedSynthesisInput, id: string, fallback: number): number {
  const value = input.controls[id];

  return typeof value === "number" ? clamp01(value) : fallback;
}

function boreModes(baseFrequency: number, variantBrightness: number, oddBias = 0.4): ResonatorSpec[] {
  return [
    { frequency: baseFrequency * 1.01, q: 3.2, gainDb: 3.2, type: "peaking" },
    { frequency: baseFrequency * (2.02 + oddBias * 0.08), q: 2.6, gainDb: 2.4, type: "peaking" },
    { frequency: baseFrequency * (3.01 + oddBias * 0.14), q: 2.0, gainDb: 1.8, type: "peaking" },
    { frequency: 1200 + variantBrightness * 2100, q: 1.4, gainDb: 1.5, type: "peaking" }
  ];
}

function edgeToneSources(
  input: AdvancedSynthesisInput,
  destination: AudioNode,
  options: {
    brightness: number;
    breathPressure: number;
    overblow?: boolean;
    attack: number;
    sustain: number;
    peakGain: number;
  }
): {
  oscillator: OscillatorNode;
  edge: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  cleanupNodes: AudioNode[];
} {
  const context = input.context;
  const oscillator = context.createOscillator();
  const edge = context.createOscillator();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  const register = options.overblow ? 2.01 : 1;

  oscillator.type = "triangle";
  edge.type = "sine";
  oscillator.frequency.setValueAtTime(input.frequency * register, input.now);
  edge.frequency.setValueAtTime(input.frequency * (register * 2.01 + options.breathPressure * 0.08), input.now);
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(1200 + options.brightness * 5200, input.now);
  filter.Q.setValueAtTime(0.45 + options.breathPressure * 1.4, input.now);
  gain.gain.value = 0.0001;
  scheduleSafeEnvelope(gain.gain, context, {
    attack: options.attack,
    decay: 0.16,
    sustain: options.sustain,
    release: 0.28,
    peakGain: options.peakGain,
    startTime: input.now
  });
  oscillator.connect(filter);
  edge.connect(filter);
  filter.connect(gain);
  gain.connect(destination);

  return { oscillator, edge, filter, gain, cleanupNodes: [filter, gain] };
}

export function renderDaegeum(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("daegeum", input.variantId);
  const breathPressure = controlNumber(input, "breathPressure", 0.55);
  const cheongExposure = controlNumber(input, "cheongExposure", 0.52);
  const cheongTension = controlNumber(input, "cheongTension", 0.5);
  const cheongThreshold = clamp(0.48 + cheongTension * 0.24 - cheongExposure * 0.22, 0.22, 0.82);
  const cheongAmount = clamp((breathPressure - cheongThreshold) / Math.max(0.08, 1 - cheongThreshold), 0, 1);
  const brightness = clamp01((variant?.brightness ?? 0.5) + breathPressure * 0.16 + cheongAmount * 0.12);
  const gain = context.createGain();
  const boreInput = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, boreInput];
  const tone = edgeToneSources(input, boreInput, {
    brightness,
    breathPressure,
    attack: 0.028 + (1 - (variant?.attack ?? 0.5)) * 0.04,
    sustain: 0.78,
    peakGain: input.velocity * (0.35 + breathPressure * 0.22)
  });
  const breath = createNoiseBurst(context, {
    duration: Math.max(1.2, input.duration ?? 2.3),
    attack: 0.025,
    decay: 0.36,
    gain: breathPressure * input.velocity * (0.024 + (variant?.noiseGain ?? 0.35) * 0.05),
    filterType: "bandpass",
    frequency: 1100 + brightness * 5800,
    q: 0.65,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "tilted-breath"),
    tilt: "bright",
    startTime: input.now
  });

  gain.gain.value = 0.95;
  tone.oscillator.start(input.now);
  tone.edge.start(input.now);
  breath.output.connect(boreInput);
  breath.source.start(input.now);
  boreInput.connect(gain);
  cleanupNodes.push(...tone.cleanupNodes, ...breath.cleanupNodes);
  cleanupNodes.push(...createBodyResonatorBank(context, boreInput, gain, boreModes(input.frequency, brightness, 0.2), variant?.bodyGain ?? 0.7));
  sources.push(tone.oscillator, tone.edge, breath.source);

  if (cheongAmount > 0.001) {
    const rattle = createNoiseBurst(context, {
      duration: Math.max(0.5, input.duration ?? 1.4),
      attack: 0.006,
      decay: 0.9,
      gain: input.velocity * cheongAmount * (0.035 + cheongExposure * 0.06),
      filterType: "highpass",
      frequency: 1800 + cheongTension * 4200,
      q: 0.8 + cheongExposure * 5,
      seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "cheong-rattle"),
      tilt: "bright",
      startTime: input.now + 0.012
    });
    const peak = context.createBiquadFilter();

    peak.type = "peaking";
    peak.frequency.setValueAtTime(4500 + cheongExposure * 6800, input.now);
    peak.Q.setValueAtTime(3 + cheongTension * 8, input.now);
    peak.gain.setValueAtTime(3 + cheongExposure * 8, input.now);
    rattle.output.connect(peak);
    peak.connect(gain);
    rattle.source.start(input.now + 0.012);
    sources.push(rattle.source);
    cleanupNodes.push(...rattle.cleanupNodes, peak);
  }

  if (input.ornament === "yoseong") {
    tone.oscillator.detune.setValueAtTime(0, input.now);
    tone.oscillator.detune.linearRampToValueAtTime(18 + breathPressure * 18, input.now + 0.18);
    tone.oscillator.detune.linearRampToValueAtTime(-10, input.now + 0.36);
    tone.oscillator.detune.linearRampToValueAtTime(14 + breathPressure * 12, input.now + 0.54);
    tone.filter.frequency.linearRampToValueAtTime(2200 + brightness * 5600, input.now + 0.18);
    tone.filter.frequency.linearRampToValueAtTime(1300 + brightness * 4600, input.now + 0.42);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.32,
    debug: {
      family: "edge_tone_bore",
      model: "daegeum_edge_bore_cheong",
      cheongAmount,
      layers: ["edge_tone", "tilted_breath", "bamboo_bore", cheongAmount > 0 ? "threshold_cheong_rattle" : "cheong_closed"]
    }
  });
}

function neyModes(input: AdvancedSynthesisInput, brightness: number): ModalSpec[] {
  const persian = input.variantId === "persian_ney";

  return [
    { ratio: 1, gain: 0.04, decay: 0.45, detuneCents: persian ? 3 : 0 },
    { ratio: 2.01, gain: 0.028 + brightness * 0.018, decay: 0.36 },
    { ratio: 3.02, gain: persian ? 0.034 : 0.018, decay: 0.28 },
    { ratio: 4.03, gain: persian ? 0.025 : 0.012, decay: 0.22 },
    { ratio: 5.04, gain: 0.011 + brightness * 0.012, decay: 0.18 }
  ];
}

export function renderNey(input: AdvancedSynthesisInput): AdvancedVoiceHandle {
  const context = input.context;
  const variant = getVariantModelProfile("ney", input.variantId);
  const breathPressure = controlNumber(input, "breathPressure", 0.55);
  const microbend = controlNumber(input, "neyMicrobend", controlNumber(input, "microBendAmount", 0.35));
  const overblow = input.ornament === "overblow";
  const brightness = clamp01((variant?.brightness ?? 0.45) + microbend * 0.16 + breathPressure * 0.1);
  const gain = context.createGain();
  const tubeInput = context.createGain();
  const sources: AudioScheduledSourceNode[] = [];
  const cleanupNodes: AudioNode[] = [gain, tubeInput];
  const tone = edgeToneSources(input, tubeInput, {
    brightness,
    breathPressure,
    overblow,
    attack: 0.035 + (input.variantId === "turkish_ney" ? 0.04 : 0),
    sustain: 0.78 + (input.variantId === "turkish_ney" ? 0.08 : 0),
    peakGain: input.velocity * (0.28 + breathPressure * 0.22)
  });
  const breath = createNoiseBurst(context, {
    duration: Math.max(1.2, input.duration ?? 2.4),
    attack: 0.028,
    decay: 0.42,
    gain: input.velocity * (0.03 + breathPressure * (variant?.noiseGain ?? 0.5) * 0.08),
    filterType: "bandpass",
    frequency: input.variantId === "persian_ney" ? 4200 + brightness * 2200 : 1600 + brightness * 3800,
    q: input.variantId === "persian_ney" ? 1.5 : 0.78,
    seed: hashSeed(input.instrumentId, input.variantId, input.noteLabel, "ney-breath"),
    tilt: "bright",
    startTime: input.now
  });
  const modal = createModalBank(context, input.frequency, neyModes(input, brightness), {
    destination: gain,
    velocity: input.velocity,
    now: input.now + 0.02,
    family: "edge_tone_bore",
    quality: input.quality,
    totalGain: 0.8,
    duration: input.duration ?? 2.4
  });

  gain.gain.value = 0.96;
  tone.oscillator.start(input.now);
  tone.edge.start(input.now);
  breath.output.connect(tubeInput);
  breath.source.start(input.now);
  tubeInput.connect(gain);
  cleanupNodes.push(...tone.cleanupNodes, ...breath.cleanupNodes, ...modal.cleanupNodes);
  cleanupNodes.push(...createBodyResonatorBank(context, tubeInput, gain, boreModes(input.frequency, brightness, input.variantId === "persian_ney" ? 0.62 : 0.35), variant?.bodyGain ?? 0.66));
  sources.push(tone.oscillator, tone.edge, breath.source, ...modal.sources);

  if (input.ornament === "micro_bend" || input.ornament === "bend") {
    const bend = 10 + microbend * 52;

    tone.oscillator.detune.linearRampToValueAtTime(bend, input.now + 0.16);
    tone.oscillator.detune.linearRampToValueAtTime(-bend * 0.32, input.now + 0.3);
    tone.oscillator.detune.linearRampToValueAtTime(0, input.now + 0.48);
    tone.filter.frequency.linearRampToValueAtTime(1800 + brightness * 5600 + microbend * 1600, input.now + 0.18);
    tone.filter.frequency.linearRampToValueAtTime(1400 + brightness * 4200, input.now + 0.42);
  }

  return createAdvancedVoice(input, {
    gain,
    sources,
    cleanupNodes,
    releaseSeconds: 0.32,
    debug: {
      family: "edge_tone_bore",
      model: "ney_end_blown_edge_tube",
      variantTags: variant?.metadataTags,
      layers: ["edge_tone", "breath_turbulence", "tube_resonator_bank", overblow ? "register_switch" : "first_register"]
    }
  });
}
